import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DateTimePickerDirective } from 'src/app/directives/datepicker.directive';

import { InputColumns, InputTypes } from 'src/app/models/input-columns';
import { FooterRow, Formats, TableColumn, TableFilter, TableFilterRow } from 'src/app/models/table-column';
import { DTColumn, JQueryDataTableParamModel, TableRequest } from 'src/app/models/table-request';
import { TransportOrderControlType } from 'src/app/models/transport-order-control-types';
import { GenericTableService } from 'src/app/services/generic-table.service';
import { TranslateValueService } from 'src/app/services/translate-value.service';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DecimalPipe } from '@angular/common';
import { FileTreatmentFunctions } from 'src/app/modules/treatments.module';
import { convertToDictionary, CURRENCY_DECIMAL, format, QUANTITY_DECIMAL } from 'src/app/constants/global';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';

const ELEMENT_DATA: TransportOrderControlType[] = [
    { ID: 1, OrderType: 'A', PrefixProductCode: "", LocationID: "77", WmsControlCode: "teste" },
    { ID: 2, OrderType: 'B', PrefixProductCode: "", LocationID: "77", WmsControlCode: "teste" },
    { ID: 3, OrderType: 'C', PrefixProductCode: "", LocationID: "77", WmsControlCode: "teste" },
    { ID: 4, OrderType: 'D', PrefixProductCode: "", LocationID: "77", WmsControlCode: "teste" },
    { ID: 4, OrderType: 'D', PrefixProductCode: "", LocationID: "77", WmsControlCode: "teste" },
    { ID: 4, OrderType: 'D', PrefixProductCode: "", LocationID: "77", WmsControlCode: "teste" },
    { ID: 4, OrderType: 'D', PrefixProductCode: "", LocationID: "77", WmsControlCode: "teste" },
    { ID: 4, OrderType: 'D', PrefixProductCode: "", LocationID: "77", WmsControlCode: "teste" },
    { ID: 4, OrderType: 'D', PrefixProductCode: "", LocationID: "77", WmsControlCode: "teste" },
    { ID: 4, OrderType: 'D', PrefixProductCode: "", LocationID: "77", WmsControlCode: "teste" },
    { ID: 4, OrderType: 'D', PrefixProductCode: "", LocationID: "77", WmsControlCode: "teste" },
    { ID: 4, OrderType: 'D', PrefixProductCode: "", LocationID: "77", WmsControlCode: "teste" },
];


@Component({
    selector: 'generic-table',
    templateUrl: './generic-table.component.html',
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    styles: [
        '.element-row td { border-bottom-width: 0;}',
        '.element-detail { overflow: hidden; display: flex;}',
        'tr.detail-row {height: 0;}',
        '.element-row-detail {padding: 0px !important}',
        '.w-75 {width: 75px;}',
        '.w-30 {width: 30px;}',
        'td.mat-mdc-cell:first-of-type {padding-left: 8px; }',
        ':host ::ng-deep .mat-sort-header-content { width:100% }',
        '.text-right { width:100%; }',
        '.mb-0 { margin-bottom:0px !important }',
        '.mb-16 { margin-bottom:16px !important }',
        '.h-40 { height:40px !important }',
        `
         /* TODO(mdc-migration): The following rule targets internal classes of paginator that may no longer apply for the MDC version. */
         :host ::ng-deep .mat-paginator-page-size-select { margin-top: -14px !important}`,
        'td.mat-mdc-cell:last-of-type { padding-right: 8px}',
        'th.mat-mdc-header-cell:last-of-type { padding-right: 8px !important }',
        'th.mat-mdc-header-cell:first-of-type { padding-left: 8px !important }'
    ],
    standalone: false
})


export class GenericTableComponent implements OnInit, AfterViewInit, OnDestroy,OnChanges {
    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort: MatSort;
    @ViewChild("expandIcon", { static: false }) expandIcon: any;
    @ViewChild("headerCheckbox", { static: false }) headerCheckbox: MatCheckbox;

    //Dados para mostrar na Tabela
    @Input("data") data: any;
    //Request para ser Executado no caso de não existir dataset
    @Input("request") request: TableRequest = null;
    //Request para ser Executado no caso de não existir dataset
    @Input("requestDownload") requestDownload: TableRequest = null;
    //Colunas a serem apresentadas no ecrã
    @Input("displayedColumns") displayedColumns: TableColumn[] = null
    //Colunas que Irão conter Inputs
    @Input("inputColumns") inputColumns: InputColumns[] =
        [{ Name: "OrderType", InputType: InputTypes.Date, ListOptionsCombobox: { List: ELEMENT_DATA, ShowProperty: "OrderType", ValueProperty: "ID" } }];
    
    //Flag para ativar as checkboxes no inicio da tabela
    @Input("checkbox") checkbox: boolean = false;

    //Flag para ativar as checkbox no fim da tabela
    @Input("showCheckboxOnEnd") showCheckboxOnEnd: boolean = false;

    //Flag para ativar a tabela expansivel
    @Input("expandable") expandable: boolean = false;
    //Conteudo de um Ng-template passado pelo componente pai para ser apresentado quando é expandido uma row
    @Input("expandableContent") expandableContent: TemplateRef<any>;

    //Aplica alterações no paginator, remove margins, fica mais pequeno e com border
    @Input("paginatorBorder") paginatorBorder: boolean = false;

    //Conteudo de um Ng-template passado pelo componente pai para ser apresentado na coluna das actions
    @Input("extraActions") extraActions: TemplateRef<any>;

    //Usado para esconder os botões de filtro e Limpar, dependente da var actions
    @Input("hideHeaderActions") hideHeaderActions: boolean = false;

    // Geral para toda a Tabela
    @Input("allowEdit") allowEdit: boolean = false;

    // Apenas para as checkbox
    @Input("allowEditCheckBox") allowEditCheckBox: boolean = false;

    @Input("allowExport") allowExport: boolean = false;

    @Input("allowExportSEPA") allowExportSEPA: boolean = false;

    // Geral para toda a Tabela
    @Input("allowDelete") allowDelete: boolean = false;

    // Geral para toda a Tabela
    @Input("allowView") allowView: boolean = true;
    
    @Input("actions") actions: boolean = true;

    @Input("pageSize") pageSize: number = 25;

    /** colunas selecionadas por defeito quando a tabela é carregada, colocar o nome da coluna que vai ter um key unica, para que consiga selecionar as colunas corretas */
    @Input("selectedRows") alreadySelectedRows: { key?: string, alreadySelectedKeys: any[] } = { key: 'ID', alreadySelectedKeys: [] };

    @Input("footerRows") footerRows: FooterRow[] = [];

    @Input() formGroup: FormGroup;
    @Input() formArrayName: string;

    //Evento que irá ser chamado pelo componente pai para poder editar
    @Output("edit") editRow: EventEmitter<number> = new EventEmitter();

    //Evento que irá ser chamado pelo componente pai para poder editar
    @Output("exportSEPA") exportSEPA: EventEmitter<number> = new EventEmitter();
    
    // enviar a row inteira
    @Output("onClickEditRow") onClickEditRow: EventEmitter<any> = new EventEmitter();

    @Output("onClickExportSEPA") onClickExportSEPA: EventEmitter<any> = new EventEmitter();

    //Evento que irá ser chamado pelo componente pai para poder eliminar
    @Output("delete") deleteRow: EventEmitter<number> = new EventEmitter();

    // enviar a row inteira
    @Output("onClickDeleteRow") onClickDeleteRow: EventEmitter<any> = new EventEmitter();

    //Evento que irá ser chamado pelo componente pai para poder obter as linhas Selecionadas
    @Output("selectedRows") selectedRows: EventEmitter<any> = new EventEmitter();

    @Output("onChangeSelect") onChangeSelectBox: EventEmitter<any> = new EventEmitter();
    @Output("onChangeInput") onChangeInput: EventEmitter<any> = new EventEmitter();
    @Output("onExport") onExport: EventEmitter<any> = new EventEmitter();

    //InputType RouteClick, envia a row
    @Output("onRouteClick") onRouteClick: EventEmitter<any> = new EventEmitter();

    //AVISA o componente pai para fazer reload da informação
    //Utilizado no botão de estados, isto porque o 'this' dentro da função é relativo À função e não ao componente Pai
    //logo é passado o this da genericTable para chamar o reloadData para avisar o componentePai para atualizar
    @Output("reloadData") reloadData: EventEmitter<boolean> = new EventEmitter();

    @Input() sortFunction: (data: any, sortHeaderId: string) => string | number;

    @Input() showSelectAllCheckbox: boolean = true;

    emptyData = new MatTableDataSource([{ empty: "row" }]);
    
    destroy$: Subject<boolean> = new Subject<boolean>();

    //Variavel que vai conter todos os Atributos associados à variavel array
    columnsOfObject: TableFilterRow[];
    //Variavel que vai conter os dados da table e suas definições
    dataSource: MatTableDataSource<any>;
    //Variavel que vai conter o Array das linhas selecionadas
    selection = new SelectionModel<any>(true, []);

    expandedElement: any | null;

    columnsValues: string[];

    filterServer: boolean

    form: FormGroup;

    _decimalPipe: DecimalPipe;

    constructor(private genericService: GenericTableService,
        public translateService: TranslateService ,
        public _translateValueService : TranslateValueService, 
        public _formBuilder : FormBuilder,
        public _fileTreat: FileTreatmentFunctions,
        public _dialog : MatDialog) {

        this._decimalPipe = new DecimalPipe(translateService.currentLang);
    }


     //Vai buscar os dados atuais da table para enviar para o BackEnd
    get tablefilterForRequest() : TableFilter{
        const filters = this.columnsOfObject.filter(filter => filter.FilterValue ||  filter.SortDirection);

        filters.forEach((filter, index, array) => array[index].FilterValue = array[index].FilterValue?.toString());

        return new TableFilter(this.paginator.pageIndex+1, this.paginator.pageSize, filters);
    }

    get tablefilterAllDataForRequest() : TableFilter{
        return new TableFilter(1, this.paginator.length, this.columnsOfObject.filter(filter => filter.FilterValue));
    }

    get noData() {
        return this.dataSource.filteredData?.length === 0;
    }


    async ngOnInit(isInit: boolean = true) {
        //Se não existir dataset e existir Request ele executa o Request para ir buscar os dados
        if (this.data == null && this.request != null) {
            this.filterServer = true;

            //Still Building...
            //this.request.JQueryDataTableParamModel = new JQueryDataTableParamModel(1, 0, 10, null, null, this.getDTColumns());
            let tableFilter = new TableFilter(1, this.pageSize, this.displayedColumns.filter(dc => dc.DefaultFilterValue).map(dc => new TableFilterRow(dc.ColumnValue, dc.DefaultFilterValue?.toString())) )

            let result = await this.genericService.request(this.request, tableFilter).toPromise();
            if (result.ReturnStatus.Successfull) {
                let infoPage = result.ReturnStatus.ReturnObject;
                if(infoPage){
                    this.data = infoPage.Items;
                    this.paginator.length = infoPage.TotalItems; 
                }
            }
            else
                console.warn(result.ReturnStatus.ErrorMessage);
        }
        
        //Para o Caso em que a Tabela nao tem dados preencher a entidade dos filtros
        if((!this.data || this.data.length == 0 || (this.data instanceof AbstractControl && (<FormArray>this.data).controls.length < 1)) && this.displayedColumns)
        {

            this.columnsOfObject = this.displayedColumns.filter(x => x.ColumnValue != 'actions' && x.ColumnValue != 'select' && x.ColumnValue != 'expand').map(e => 
            {
                return new TableFilterRow(e.ColumnValue, e.DefaultFilterValue?.toString() ?? "",e.InputType, e.SelectList, e.AlignRight, e.Format);
            })

            this.addExtraColumns();

            //2024-01-18 GM para a tabela não rebentar se não tiver dados
            this.dataSource = new MatTableDataSource([]);

            if (this.paginator){
                this.paginator.length = 0;
            }

            //Se não tiver preenchido, preenche com base no displayedColumns
            //Se a tabela não tiver dados ao inicializar, sem este código a tabela fica toda estragada porque não tem colunas
            if (!this.columnsOfObject || this.columnsOfObject.length == 0){
                this.columnsOfObject = this.displayedColumns.map(x => {
                    return new TableFilterRow(x.ColumnName, "");
                })
            }
        }

        else if (this.data != null && this.data.length > 0) {
            //Primeiro valor do array que contem todas as propriedades para preencher as colunas
            let valueWithObjectKeys;
            if(this.data instanceof AbstractControl)
            {
                let rawValueForm = (<FormArray>this.data).getRawValue();
                valueWithObjectKeys = rawValueForm[0];
                this.dataSource = new MatTableDataSource(rawValueForm);

                this.form = this.formGroup ?? this._formBuilder.group({ array: this.data });
            }
            else
            {
                valueWithObjectKeys = this.data[0];
                this.dataSource = new MatTableDataSource(this.data);
            }

            if(valueWithObjectKeys)
            {
                this.displayedColumns.forEach(column => {
                    if(column.ColumnValue == 'actions' || column.ColumnValue == 'select' || column.ColumnValue == 'expand'){}
                    else{
                        let columnExist = Object.keys(valueWithObjectKeys).find(e => column.ColumnValue == e);
                        if(!columnExist)
                            console.warn("A coluna das displayedColumns "+column.ColumnValue+" não existe na entidade")
                    }
                })

                //Apenas cria filtros quando é inicializado
                if (isInit){
                    this.columnsOfObject = Object.keys(valueWithObjectKeys).map(e => {
                        let displayedColumn = this.displayedColumns.find(col => col.ColumnValue === e);

                        if (displayedColumn)
                            return new TableFilterRow(e, displayedColumn.DefaultFilterValue?.toString() ?? "", displayedColumn.InputType, displayedColumn.SelectList, displayedColumn.AlignRight,displayedColumn.Format);
    
                        return new TableFilterRow(e, "");
                        
                    });
                }
            }


            let columnsToFormat = this.columnsOfObject?.filter(co => co.Format);

            columnsToFormat.forEach(columnToFormat => {
                this.data.forEach(data => {

                    switch (columnToFormat.Format) {
                        case Formats.Date:
                            data[columnToFormat.ObjectKey] = DateTimePickerDirective.convertToString(data[columnToFormat.ObjectKey], false);
                            break;

                        case Formats.DateTime:
                            data[columnToFormat.ObjectKey] = DateTimePickerDirective.convertToString(data[columnToFormat.ObjectKey], true);
                            break;

                        case Formats.Boolean:
                            if (typeof data[columnToFormat.ObjectKey] == "boolean") {
                                data[columnToFormat.ObjectKey] = this._translateValueService.get(data[columnToFormat.ObjectKey] ? 'YES' : 'NO')
                            }else{
                                data[columnToFormat.ObjectKey] = this._translateValueService.get(data[columnToFormat.ObjectKey]?.toLowerCase() == 'true' ? 'YES' : 'NO')
                            }
                            break;
                        case Formats.Quantity:
                            data[columnToFormat.ObjectKey] = (data[columnToFormat.ObjectKey]?.toString()?.revertDecimal() ?? 0).formatDecimal(this._translateValueService.translateLanguage, +QUANTITY_DECIMAL)
                            break;
                        case Formats.Currency:
                            data[columnToFormat.ObjectKey] = (data[columnToFormat.ObjectKey]?.toString()?.revertDecimal() ?? 0)?.formatDecimal(this._translateValueService.translateLanguage, +CURRENCY_DECIMAL)
                            break;
                    }

                });
            })            

            this.addExtraColumns();
            this.initCheckBoxSelection()


            this.generateFooterColumns();
        }

        this.generateFooterColumns();

        this.columnsValues = this.displayedColumns?.map(e => e.ColumnValue);


        if (this.dataSource)
            this.dataSource.filterPredicate = this.filterPredicate();
        
        if (this.filterServer) {
            this.sort.sortChange.subscribe((sort: Sort) => {
                const sortedColumn = this.columnsOfObject.find(col => col.ObjectKey === sort.active);

                if (sortedColumn) {
                    this.columnsOfObject.forEach(col => col.SortDirection = null);
                    sortedColumn.SortDirection = sort.direction;

                    this.filter()
                }
            });
        }
    }

    private generateFooterColumns() {
        let footerRowIndex = 0;

        /* this.footerRows.push({
            Condition: !this.dataSource?.data?.length,
            FooterColumns: [{
                FooterColumnName: 'footer-column-no-data',
                Value: `<div class="textAlignCenter">${ this._translateValueService.get('NO_RESULTS_WERE_FOUND') }</div>`,
                Colspan: this.displayedColumns.length
            }],
        }) */

        this.footerRows.forEach((row) => {
            if(row.Condition)
            {
                row.FooterColumns.forEach((column) => {
                    column.FooterColumnName = 'footer-column-' + footerRowIndex;
                    footerRowIndex++;
                });
    
                row.ColumsOrder = row.FooterColumns.map((column) => column.FooterColumnName);
            }
        });
    }

    format(data: any) {
        if (data != null && data.length > 0) {
         
            let columnsToFormat = this.columnsOfObject.filter(co => co.Format);

            columnsToFormat.forEach(columnToFormat => {
                data.forEach(datax => {

                    switch (columnToFormat.Format) {
                        case Formats.Date:
                            datax[columnToFormat.ObjectKey] = DateTimePickerDirective.convertToString(datax[columnToFormat.ObjectKey], false);
                            break;
                        case Formats.DateTime:
                            datax[columnToFormat.ObjectKey] = DateTimePickerDirective.convertToString(datax[columnToFormat.ObjectKey], true);
                            break;
                        case Formats.Boolean:
                            if (typeof datax[columnToFormat.ObjectKey] == "boolean") {
                                datax[columnToFormat.ObjectKey] = this._translateValueService.get(datax[columnToFormat.ObjectKey] ? 'YES' : 'NO')
                            }else{
                                datax[columnToFormat.ObjectKey] = this._translateValueService.get(datax[columnToFormat.ObjectKey]?.toLowerCase() == 'true' ? 'YES' : 'NO')
                            }
                            break;
                        case Formats.Quantity:
                            data[columnToFormat.ObjectKey] = (data[columnToFormat.ObjectKey]?.toString()?.revertDecimal() ?? 0).formatDecimal(this._translateValueService.translateLanguage, +QUANTITY_DECIMAL)
                            break;
                        case Formats.Currency:
                            data[columnToFormat.ObjectKey] = (data[columnToFormat.ObjectKey]?.toString()?.revertDecimal() ?? 0).formatDecimal(this._translateValueService.translateLanguage, +CURRENCY_DECIMAL)
                            break;
                    }

                });
            })
        }
    }

    ngAfterViewInit(): void {
        if (this.data != null && this.data.length) {
            if(this.paginator)
                this.paginator._intl.itemsPerPageLabel = this._translateValueService.get("RESULTS_PER_PAGE");
            this.dataSource.paginator = this.paginator;

            // coluna para filtrar por defeito
            const columnToSortByDefault = this.displayedColumns.find(dc => dc.DefaultSort);
            if (columnToSortByDefault)
            {
                this.sort.sort(({ id: columnToSortByDefault.ColumnValue, start: columnToSortByDefault.DefaultSort }) as MatSortable)
            }


            if(this.sortFunction) {
                this.dataSource.sortingDataAccessor = this.sortFunction;
            }
            

            this.dataSource.filter

            this.dataSource.sort = this.sort;

            if (this.paginator)
                this.updateHeaderCheckbox();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
            
        if(!this.request && changes.data && changes.data.previousValue && changes.data.currentValue)
        {
            this.ngOnInit(false);
            this.ngAfterViewInit();

            //Aplica os filtros novamente sobre a nova data
            this.filter();
        }
        
        if (this.dataSource?.data && changes.alreadySelectedRows?.currentValue || changes.alreadySelectedRows?.previousValue) {
            const key = this.alreadySelectedRows.key;

            let currentSelected = changes.alreadySelectedRows?.currentValue as { key?: string, alreadySelectedKeys: any[] };

            // se estiverem a null inicializar
            if(!currentSelected?.alreadySelectedKeys)
                currentSelected = {key, alreadySelectedKeys: []};

            this.selection.clear();

            const unselected = this.selection.selected.filter(row => !currentSelected.alreadySelectedKeys.includes(row)) ;
            const selected = currentSelected.alreadySelectedKeys;

            unselected.forEach(selectedRow => {
                const row = this.dataSource?.data.find(data => data[key] === selectedRow[key]);
                if (row) { this.selection.deselect(row); }
            });

            selected.forEach(selectedRow => {
                const row = this.dataSource?.data.find(data => data[key] === selectedRow[key]);
                if (row) { this.selection.select(row); }
            });
        }
    }

    edit(row: any) {
        this.editRow.emit(row.ID);
        this.onClickEditRow.emit(row);
    }

    
    exportSEPAClick(row: any) {
        this.exportSEPA.emit(row.ID);
        this.onClickExportSEPA.emit(row);
    }

    delete(tableRow: any) {
        if (this.form) {
            this.dataSource.data = this.dataSource.data.filter(row => row.ID != tableRow.ID);
        }

        this.deleteRow.emit(tableRow.ID);
        this.onClickDeleteRow.emit(tableRow);
    }

    export(tableRow: any) {
        this.onExport.emit(tableRow);
    }

    //Função para filtar client side
    //Como filtrar: this.dataSource.filter =  JSON.stringify(INSERT_HERE)
    //return true -> filtra
    //return false -> não filtra
    filterPredicate() {
        return (row: any, filters: string): boolean => {
            let searchObjects = JSON.parse(filters);

            for (let searchObject of searchObjects) {
                if (searchObject["FilterValue"]) {
                    if (row[searchObject["ObjectKey"]].toString().trim().toLowerCase().indexOf(searchObject["FilterValue"].toString().trim().toLowerCase()) === -1){
                        return false
                    }
                }
            }

            return true;
        };
    }

    //Parametro só afeta quando é backend (filterServer = true)
    filter(resetPageIndex: boolean = false) {
        if (this.data && this.data.length > 0 && !this.filterServer) {
            // validar se os filtros estao vazios
            let found = this.columnsOfObject.filter(filter => filter.FilterValue);
            if (found?.length > 0) // se encontrou filtro filtrar
            {
                this.dataSource.filter = JSON.stringify(this.columnsOfObject);

                if(this.footerRows.length)
                {
                    let found = this.footerRows.find(row => row.FooterColumns.find(column => column.FooterColumnName == 'footer-column-no-data'));
                    if(found)
                        found.Condition = this.dataSource.filteredData.length === 0;
                }
            }
            else // se nao limpar filtros
                this.dataSource.filter = '';
        }
        else if (this.filterServer) {

            //Quando é feita uma pesquisa, é preciso dar reset ao paginador
            //Se estiver na quarta página e a pesquisa só devolver 2 registos, não mostra nada porque estou na quarta página e as linhas estão na 1ª
            if (resetPageIndex)
                this.paginator.pageIndex = 0;

            this.genericService.request(this.request, this.tablefilterForRequest).pipe(takeUntil(this.destroy$))
            .subscribe(result =>{
                if (result.ReturnStatus.Successfull) {
                    let infoPage = result.ReturnStatus.ReturnObject;

                    if (!this.dataSource){
                        this.dataSource = new MatTableDataSource([]);
                    }

                    if(infoPage){
                        this.format(infoPage.Items);
                        this.dataSource.data = infoPage.Items;
                        this.paginator.length = infoPage.TotalItems; 
                    }
                }

                if(this.footerRows.length)
                {
                    let found = this.footerRows.find(row => row.FooterColumns.find(column => column.FooterColumnName == 'footer-column-no-data'));
                    if(found)
                        found.Condition = this.dataSource.filteredData.length === 0;
                }

                this.initCheckBoxSelection()
            });
        }
    }

    getFilters(){
        if (this.data && this.data.length > 0 && !this.filterServer) {
            let found = this.columnsOfObject.filter(filter => filter.FilterValue);

            if (found.length == 0){
                return {};
            }

            return convertToDictionary(found);
        }
    }

    //Se for client side, retorna dicionário 
    downloadExcelList(){
        // validar se os filtros estao vazios

        if (this.data && this.data.length > 0 && !this.filterServer) {
            return this.getFilters();
        }
        else if (this.filterServer){
            this.genericService.requestDownload(this.requestDownload, this.tablefilterForRequest).pipe(takeUntil(this.destroy$)).subscribe((response) =>{
                this._fileTreat.afterDownloadFile(response, 'ExpenseNotes_'+this.requestDownload.Body['firstDate'] + '-'+ this.requestDownload.Body['lastDate']  +'.xlsx');
            });
        }
    }

    clearFilters(body? : any) {

        if(this.filterServer && this.request){
            this.request.Body = body
        }

        this.columnsOfObject.forEach((column) => {
            column.FilterValue = '';
        })
        this.filter();
    }

    searchButton(body){
        if(this.filterServer && this.request){
            this.request.Body = body
        }

        this.filter();
    }

    //valida se todas as linhas já foram selecionadas
    isAllSelected() {
        const numSelected = this.filterServer ? this.alreadySelectedRows.alreadySelectedKeys?.length : this.selection.selected.length;
        const numRows = this.filterServer ? this.paginator.length : this.dataSource.data.length;
        return numSelected === numRows;
    }

    // Seleciona todas as linhas, se já tiverem todas selecionadas limpa
    masterToggle(ev: MatCheckboxChange) {
        if (!this.alreadySelectedRows.alreadySelectedKeys)
            this.alreadySelectedRows.alreadySelectedKeys = [];

        if(this.filterServer){
            if(!ev.checked && !ev.source.indeterminate){
                this.dataSource.data.forEach(element => {
                    this.selection.deselect(element);

                    let index = this.alreadySelectedRows.alreadySelectedKeys.findIndex(x => x[this.alreadySelectedRows.key] == element[this.alreadySelectedRows.key])

                    if (index != -1){
                        this.alreadySelectedRows.alreadySelectedKeys.splice(index, 1);
                    }
                });
                
                //this.selection.clear()
                //this.alreadySelectedRows.alreadySelectedKeys = [];
            } else {
                
                this.dataSource.data.forEach(rowInTable => {
                    this.selection.select(rowInTable);

                    if(this.alreadySelectedRows.alreadySelectedKeys && this.alreadySelectedRows.key && !this.alreadySelectedRows.alreadySelectedKeys.find(e => e[this.alreadySelectedRows.key] == rowInTable[this.alreadySelectedRows.key]))
                        this.alreadySelectedRows.alreadySelectedKeys.push(rowInTable);
                })

                //Enviar o evento para o Componente pai para avisar que foi selecionado
                this.selectedRows.emit({selectedRows: [...(this.alreadySelectedRows?.alreadySelectedKeys || [])], allData: this.dataSource.data});
                this.updateHeaderCheckbox();
            }
        }
        else{
            this.isAllSelected() ? this.selection.clear() : this.dataSource.data.forEach(row => {
                this.selection.select(row)
                if(this.alreadySelectedRows.alreadySelectedKeys && this.alreadySelectedRows.key && !this.alreadySelectedRows.alreadySelectedKeys.find(e => e[this.alreadySelectedRows.key] == row[this.alreadySelectedRows.key]))
                    this.alreadySelectedRows.alreadySelectedKeys.push(row);
            });

            //Enviar o evento para o Componente pai para avisar que foi selecionado
            this.selectedRows.emit({selectedRows: [...this.selection.selected], allData: this.dataSource.data});
        }
    }

    updateHeaderCheckbox(){
        let indeterminate = false;
        let checked = false;

        if (this.alreadySelectedRows.alreadySelectedKeys?.length > 0){
            //Se não tiverem todos preenchidos, coloca indeterminate a true
            let length = (this.filterServer ? this.getSelectedRowsOnDataSource() : this.dataSource.data?.length) || 0

            if (length != this.alreadySelectedRows.alreadySelectedKeys?.length){
                indeterminate = true;
                checked = false;
            }else{
                indeterminate = false
                checked = true
            }
            
        }else if (this.headerCheckbox) {
            indeterminate = false
            checked = false;
        }

        setTimeout(() => {
            if (this.headerCheckbox){
                this.headerCheckbox.indeterminate = indeterminate;
                this.headerCheckbox.checked = checked;
            }
        }, 50);
    }

    getSelectedRowsOnDataSource(): number {
        let selected = 0;
        this.dataSource.data.forEach(row => {
            if (this.alreadySelectedRows.alreadySelectedKeys.some(e => e[this.alreadySelectedRows.key] == row[this.alreadySelectedRows.key]))
                selected++;
        })

        return selected;
    }

    //Evento que vai Selecionar a CheckBox
    onCheckBoxChange(row?: any) {
        this.selection.toggle(row);
        
        if(this.alreadySelectedRows.alreadySelectedKeys && this.alreadySelectedRows.key && !this.alreadySelectedRows.alreadySelectedKeys.find(e => e[this.alreadySelectedRows.key] == row[this.alreadySelectedRows.key]))
            this.alreadySelectedRows.alreadySelectedKeys.push(row);

        else if(this.alreadySelectedRows.alreadySelectedKeys && this.alreadySelectedRows.key && this.alreadySelectedRows.alreadySelectedKeys.find(e => e[this.alreadySelectedRows.key] == row[this.alreadySelectedRows.key]))
        {
            this.alreadySelectedRows.alreadySelectedKeys = this.alreadySelectedRows.alreadySelectedKeys.filter(e => e[this.alreadySelectedRows.key] != row[this.alreadySelectedRows.key]);
        }

        this.selectedRows.emit({selectedRows: [...this.selection.selected], allData: this.dataSource.data});
    }

    async selectAllFromServer() : Promise<any[]>{
        let result = await this.genericService.request(this.request,this.tablefilterAllDataForRequest).pipe(takeUntil(this.destroy$)).toPromise();
        if (result.ReturnStatus.Successfull) {
            let infoPage = result.ReturnStatus.ReturnObject;
            if(infoPage){
                return infoPage.Items;
            }
        }

        return [];
    }

    initCheckBoxSelection(){
        //Array Que vai guardar todos os selecionados
        if (this.alreadySelectedRows?.alreadySelectedKeys && this.alreadySelectedRows?.alreadySelectedKeys.length > 0) {
            const selectedKeys = this.alreadySelectedRows.alreadySelectedKeys;
            const key = this.alreadySelectedRows.key;

            if(selectedKeys){

                selectedKeys.forEach(select => {
                    if(this.dataSource.data.find(data => data[key] == select[key]) ){

                        //Quando vai ao Backend
                        //Limpar as referencias anteriormente selecionadas
                        //Para quando vier os novos dados voltar a selecionar
                        let selected = this.selection.selected.find(e => e[key] == select[key]);
                        if(this.filterServer && selected){
                            this.selection.deselect(selected);
                        }
                        
                        if(!this.selection.selected.find(data => data[key] == select[key]))
                            this.selection.select(this.dataSource.data.find(data => data[key] == select[key]));
                    }
                })

                this.selectedRows.emit({selectedRows: [...this.alreadySelectedRows.alreadySelectedKeys], allData: this.dataSource.data})
                
                if(!this.filterServer){
                    this.dataSource.data = [
                        ...this.dataSource.data.filter(value => this.selection.selected.includes(value)),
                        ...this.dataSource.data.filter(value => !this.selection.selected.includes(value))
                    ]
                }

                //Por alguma razão as vezes o headercheckbox fica a true quando está a false
                if (this.paginator)
                    this.updateHeaderCheckbox();
            }
            
        }
    }


    getArrayType() {
        //Desconstroi o Array para obter o tipo
        type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType extends readonly (infer ElementType)[] ? ElementType : never;
        let object = {} as ArrayElement<typeof ELEMENT_DATA>;

        return object;
    }

    getDTColumns() {
        let dtColumns: DTColumn[] = [];
        this.columnsValues.forEach(column => {
            dtColumns.push(new DTColumn(column, null, true, true, null));
        })

        return dtColumns;
    }

    reload(data: any) {

        if(data)
        {
            this.format(data);
        }

        if(!this.dataSource)
            this.dataSource = new MatTableDataSource(this.data);
           
        else if(data)
            this.dataSource.data = data;

    }

    isFormArray()
    {
        return this.data && this.data instanceof AbstractControl && (<FormArray>this.data).controls.length >= 1;
    }

    getFormControl(index,objectProperty)
    {
        let rowControl = (<FormArray>this.form.get(this.formArrayName ?? 'array')).controls[index]
        let propertyControlOfRow = rowControl.get(objectProperty);

        return propertyControlOfRow;
    }

    //Adiciona action, select and expand
    addExtraColumns(){
        if (this.actions && this.displayedColumns && !this.displayedColumns.find(e => e.ColumnValue == 'actions')) {
            //Adiciona a Coluna das Actions no Fim do Array
            this.displayedColumns.push({ ColumnValue: 'actions', ColumnName: 'actions' });
        }

        
        if (this.expandable && this.displayedColumns && !this.displayedColumns.find(e => e.ColumnValue == 'expand')) {
            //Adiciona a Coluna dos Icons no Inicio do Array
            this.displayedColumns.unshift({ ColumnValue: 'expand', ColumnName: 'expand' });
        }

        if (this.checkbox && this.displayedColumns && !this.displayedColumns.find(e => e.ColumnValue == 'select')) {
            //Adiciona a Coluna das Checkboxes no Inicio do Array
            const selectColumn = { ColumnValue: 'select', ColumnName: 'select' };

            if (this.showCheckboxOnEnd) {
                const actionIndex = this.displayedColumns.findIndex(col => col.ColumnValue === 'action');

                if (actionIndex == -1)
                    this.displayedColumns.push(selectColumn);
                else
                    this.displayedColumns.splice(actionIndex, 0, selectColumn);
            } else {
                this.displayedColumns.unshift(selectColumn);
            }
        }
    }

    isRowSelected(row: any): boolean {
        return this.selection.selected.some(selected => selected[this.alreadySelectedRows.key] === row[this.alreadySelectedRows.key]);
    }

    ngOnDestroy(): void {

    }

}
